import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { BankLogo } from '@/components/account/BankLogo.js';
import type { MockBank } from '@/lib/mock-data.js';

interface BankAccountRowProps {
  bank: MockBank;
  onSetDefault?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export function BankAccountRow({ bank, onSetDefault, onDelete }: BankAccountRowProps) {
  const { t } = useTranslation('common');
  return (
    <div className="grid grid-cols-[40px_1fr_auto] gap-3 items-center py-3 px-4">
      {/* Bank logo (falls back to the code text when no logo exists) */}
      <BankLogo code={bank.bankCode} size={40} />

      {/* Info */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[13px] font-semibold text-(--text-primary) truncate">
            {bank.bankName}
          </div>
          {bank.verified ? (
            <Badge tone="success">{t('badge.verified')}</Badge>
          ) : (
            <Badge tone="warning">{t('badge.pending')}</Badge>
          )}
          {bank.isDefault && <Badge tone="primary">{t('badge.default')}</Badge>}
        </div>
        <div className="font-mono text-[11px] text-(--text-muted) truncate">
          {bank.accountNumber} · {bank.accountName}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        {!bank.isDefault && onSetDefault && (
          <Button variant="ghost" size="sm" onClick={() => onSetDefault(bank.id)}>
            {t('common.setDefault')}
          </Button>
        )}
        {onDelete && (
          <Button variant="ghost" size="sm" onClick={() => onDelete(bank.id)}>
            {t('common.remove')}
          </Button>
        )}
      </div>
    </div>
  );
}
